import type { ConclusionConfig, GameDefinition } from '../types/game';
import type { GameState } from '../engine/GameState';
import { evaluateCondition, type ConclusionSelection } from './conditions';

/** Resultado de evaluar la argumentación del jugador. */
export interface ConclusionOutcome {
  ending: string;
  /** ID de la regla que decidió el desenlace, si alguna. */
  matchedRule?: number;
}

/**
 * Evalúa la mecánica central: hipótesis + evidencias + medidas → desenlace.
 *
 * La evaluación vive por completo en el JSON: las reglas usan el mismo
 * vocabulario declarativo de condiciones, con un contexto ampliado que incluye
 * la selección del jugador. El motor no conoce hipótesis ni evidencias
 * concretas; solo aplica las reglas por orden de prioridad.
 */
export function evaluateConclusion(
  config: ConclusionConfig,
  selection: ConclusionSelection,
  state: GameState
): ConclusionOutcome {
  const rules = [...config.rules].sort(
    (a, b) => (b.priority ?? 0) - (a.priority ?? 0)
  );

  for (let i = 0; i < rules.length; i++) {
    const rule = rules[i];
    const ok = evaluateCondition(rule.when, { state, conclusion: selection });
    if (ok) {
      return { ending: rule.ending, matchedRule: i };
    }
  }

  return { ending: config.fallbackEnding };
}

/** Comprueba que la selección cumpla los mínimos exigidos por el JSON. */
export function validateSelection(
  config: ConclusionConfig,
  selection: ConclusionSelection,
  game: GameDefinition
): string[] {
  const errors: string[] = [];
  if (!config.hypotheses.some((h) => h.id === selection.hypothesis)) {
    errors.push('Debes elegir una hipótesis.');
  }
  const minEvidence = config.minEvidence ?? 0;
  const validEvidence = selection.evidence.filter((e) => e in game.evidence);
  if (validEvidence.length < minEvidence) {
    errors.push(`Selecciona al menos ${minEvidence} evidencia(s) de tu diario.`);
  }
  const minMeasures = config.minMeasures ?? 0;
  const validMeasures = selection.measures.filter((m) =>
    config.measures.some((mm) => mm.id === m)
  );
  if (validMeasures.length < minMeasures) {
    errors.push(`Selecciona al menos ${minMeasures} medida(s) de actuación.`);
  }
  return errors;
}
